import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../../helpers/axiosInstance";
import useAuth from "../../../../hooks/useAuth";
import { date, fullName, money } from "../../../../helpers/format";
import { Async, Button, Card, Dialog, Empty, Field, Page, Pill, SectionTitle, useAction, useFetch } from "../../../../shared/ui/ui";
import { currentFinancialYear } from "../../../../helpers/financialYear";
import "./dashboard.css";

// Money at a glance, as the app has it: two halves on a switch at the top.
//
// Fund is the fund as a whole -- its savings, what is lent out, the interest it
// has earned, the cash it holds, this year and this month -- and, for someone
// who sees every member's invoices, everybody's dues, oldest first. Personal is
// the signed-in member's own money, whatever their role: a president saves and
// borrows like anybody else.
//
// Both halves are the role's Dashboard section: view is the page and its
// Personal half, view_all ("View fund details") the Fund half, which the server
// leaves out of the fund without it. Without view_all there is no switch.

const amountDue = (invoice) => Math.max(0, (Number(invoice.total_amount) || 0) - (Number(invoice.total_payments) || 0));
const isOverdue = (invoice) => amountDue(invoice) > 0 && invoice.due_date && Date.now() > Number(invoice.due_date);
const percent = (fraction) => (fraction == null ? "—" : `${(fraction * 100).toFixed(1)}%`);
const plural = (count, one, many) => (count === 1 ? `1 ${one}` : `${count} ${many}`);


/** "3 years 2 months", for how long somebody has been a member. */
const since = (joined) => {
  if (!joined) return null;
  const from = new Date(Number(joined) || joined);
  const now = new Date();
  let months = (now.getFullYear() - from.getFullYear()) * 12 + now.getMonth() - from.getMonth();
  if (now.getDate() < from.getDate()) months -= 1;
  if (months < 1) return "this month";
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return [years ? plural(years, "year", "years") : null, rest ? plural(rest, "month", "months") : null].filter(Boolean).join(" ");
};

const Stat = ({ label, value, sub, credit, onClick, highlight }) => (
  <div className="sf-card" style={{ cursor: onClick ? "pointer" : "default", ...(highlight ? { background: "var(--bg-inverse)", color: "var(--text-inverse)" } : {}) }} onClick={onClick}>
    <p className="sf-stat-label" style={highlight ? { color: "var(--text-inverse)", opacity: 0.7 } : undefined}>
      {label}
    </p>
    <p className="sf-stat-value">{value}</p>
    {sub ? (
      <p className="sf-muted" style={{ margin: "0.25rem 0 0", ...(highlight ? { color: "var(--text-inverse)" } : {}) }}>
        {sub}
      </p>
    ) : null}
    {/* Money that came in — a deposit's interest — small, with a credit arrow. */}
    {credit ? (
      <p style={{ margin: "0.4rem 0 0", color: "#4ade80", fontWeight: 600, fontSize: "0.85rem" }}>
        <span aria-hidden="true">↙</span> {credit}
      </p>
    ) : null}
  </div>
);

/** A link row to another page, with a figure on the right. */
const LinkRow = ({ label, value, onClick }) => (
  <Card>
    <button className="sf-row sf-row-button" style={{ border: 0, padding: 0 }} onClick={onClick}>
      <span className="sf-row-main">{label}</span>
      <strong>{value} ›</strong>
    </button>
  </Card>
);

// --- Fund ---

const FundHalf = ({ fund, dues, oversees, mayEditBalance, onEditBalance }) => {
  const navigate = useNavigate();
  return (
    <Async state={fund}>
      {(data) => {
        const m = data.metrics || {};
        const rate = m.billed_this_month > 0 ? m.paid_this_month / m.billed_this_month : null;
        return (
          <>
            <p className="sf-muted">Running since {date(data.start_date)}</p>
            <div className="sf-grid">
              <Stat highlight label="Total fund savings" value={money(data.total_saving_amount)} onClick={() => navigate("/admin/save")} />
              <Stat label="Pending loan" value={money(data.pending_loan_amount)} sub="with members" onClick={() => navigate("/admin/loans")} />
              <Stat label="Interest earned" value={money(data.total_interest_gained)} />
              <Stat
                label="Present balance"
                value={money(data.present_balance)}
                sub={
                  mayEditBalance
                    ? data.present_balance_set_at
                      ? `Set ${date(data.present_balance_set_at)} · click to correct`
                      : "Click to correct"
                    : data.present_balance_set_at
                    ? `Set ${date(data.present_balance_set_at)}`
                    : null
                }
                onClick={mayEditBalance ? () => onEditBalance(data.present_balance) : undefined}
              />
            </div>

            {data.metrics ? (
              <>
                <SectionTitle title="This year and month" />
                <p className="sf-muted">Financial year from {date(m.financial_year_start)}</p>
                <div className="sf-grid">
                  <Stat label="Deposits this FY" value={money(m.deposits_this_fy)} sub="saved by members" onClick={() => navigate("/admin/save")} />
                  <Stat
                    label="Loans given this FY"
                    value={money(m.loans_given_this_fy)}
                    sub={plural(m.loans_given_count_this_fy, "loan", "loans")}
                    onClick={() => navigate("/admin/loans")}
                  />
                  <Stat
                    label="Collected this month"
                    value={money(m.collected_this_month)}
                    sub={rate == null ? "nothing billed yet" : `${percent(rate)} of ${money(m.billed_this_month)} billed`}
                  />
                  <Stat
                    label="Overdue"
                    value={money(m.overdue_amount)}
                    sub={m.overdue_members ? `from ${plural(m.overdue_members, "member", "members")}` : "nobody is overdue"}
                  />
                  <Stat label="Active loans" value={m.active_loans} sub={`${plural(m.borrowers, "member", "members")} borrowing`} onClick={() => navigate("/admin/loans")} />
                  <Stat
                    label="Savings lent out"
                    value={percent(data.total_saving_amount > 0 ? data.pending_loan_amount / data.total_saving_amount : null)}
                    sub="pending loan against savings"
                  />
                  <Stat label="Average savings" value={money(data.total_members > 0 ? data.total_saving_amount / data.total_members : 0)} sub="per member" />
                  <Stat label="Loan applications" value={m.applications_awaiting} sub="awaiting sanction" />
                  {/* What is in each kind of deposit now, and what closed ones earned. */}
                  <Stat
                    label="Fixed deposits"
                    value={money(m.in_fixed_deposits)}
                    sub={plural(m.active_fixed_deposits, "active FD", "active FDs")}
                    credit={`${money(m.fd_interest_earned)} interest`}
                    onClick={() => navigate("/admin/fixed-deposits")}
                  />
                  <Stat
                    label="Recurring deposits"
                    value={money(m.in_recurring_deposits)}
                    sub={plural(m.active_recurring_deposits, "active RD", "active RDs")}
                    credit={`${money(m.rd_interest_earned)} interest`}
                    onClick={() => navigate("/admin/recurring-deposits")}
                  />
                </div>
              </>
            ) : null}

            {oversees ? (
              <>
                <SectionTitle title="Due now" action={dues.data?.some(isOverdue) ? <Pill tone="bad">{dues.data.filter(isOverdue).length} overdue</Pill> : null} />
                <p className="sf-muted">Every member with an invoice outstanding</p>
                <Async state={dues}>
                  {(list) =>
                    !list.length ? (
                      <Empty title="Everyone is paid up">No invoice is outstanding.</Empty>
                    ) : (
                      <Card flush>
                        {list.slice(0, 50).map((invoice) => (
                          <button key={invoice._id} className="sf-row sf-row-button" onClick={() => navigate(`/admin/invoices/${invoice._id}`)}>
                            <div className="sf-row-main">
                              <p className="sf-row-title">{invoice.member ? fullName(invoice.member) : "Member"}</p>
                              <p className="sf-row-sub">
                                {invoice.invoice_number} · due {date(invoice.due_date)}
                              </p>
                            </div>
                            <div className="sf-row-end">
                              <p className="sf-row-title">{money(amountDue(invoice))}</p>
                              {isOverdue(invoice) ? <Pill tone="bad">Overdue</Pill> : null}
                            </div>
                          </button>
                        ))}
                      </Card>
                    )
                  }
                </Async>
              </>
            ) : null}

            <SectionTitle title="The fund" />
            <LinkRow label="Members" value={data.total_members} onClick={() => navigate("/admin/members")} />
          </>
        );
      }}
    </Async>
  );
};

// --- Personal ---

const PersonalHalf = ({ seesFund, fund }) => {
  const navigate = useNavigate();
  const { auth, organisation, financialYearStartMonth, can } = useAuth();
  const me = auth.user._id;
  const fds = useFetch(`/api/fixed_deposits/${organisation._id}`, { select: (body) => body?.data || [], skip: !can("fixed_deposits") });
  const rds = useFetch(`/api/recurring_deposits/${organisation._id}`, { select: (body) => body?.data || [], skip: !can("recurring_deposits") });

  const savings = useFetch(`/api/savings/${organisation._id}/gettransactionbyid/${me}?type=all&startdate=0`, {
    select: (body) => ({ items: Array.isArray(body?.data) ? body.data : [], total: Number(body?.metadata?.net_balance) || 0 }),
  });
  const record = useFetch(`/api/member/${organisation._id}/getmember/${me}`, { select: (body) => (Array.isArray(body) ? body[0] : body) });
  const invoices = useFetch(`/api/invoice/${organisation._id}/members/${me}/invoices`, { select: (body) => (Array.isArray(body) ? body : []) });
  const loans = useFetch(`/api/loans/${organisation._id}/gettransactionbyid/${me}?active=all&startdate=0`, {
    select: (body) => (Array.isArray(body?.data) ? body.data : []),
  });

  // No bill is a 404 here, which is an answer rather than a failure.
  const [current, setCurrent] = useState({ loading: true, invoice: null });
  React.useEffect(() => {
    let live = true;
    axiosInstance
      .get(`/api/invoice/${organisation._id}/member/${me}`)
      .then((response) => live && setCurrent({ loading: false, invoice: response.data?._id ? response.data : null }))
      .catch(() => live && setCurrent({ loading: false, invoice: null }));
    return () => {
      live = false;
    };
  }, [organisation._id, me]);

  const unpaid = (invoices.data || []).filter((invoice) => invoice.status !== "paid" && amountDue(invoice) > 0);
  const dues = unpaid.reduce((sum, invoice) => sum + amountDue(invoice), 0);
  const overdue = unpaid.filter(isOverdue).length;

  // "This FY" starts in the fund's own month.
  const fyStart = new Date(currentFinancialYear(financialYearStartMonth).startdate);
  // Money back from a closed FD or RD is not something they saved.
  const deposits = (savings.data?.items || [])
    .filter((t) => t.type === "credit" && t.date && !["FIXED_DEPOSIT", "RECURRING_DEPOSIT"].includes(t.transfer_type))
    .sort((a, b) => Number(b.date) - Number(a.date));
  const savedThisFy = deposits.filter((t) => Number(t.date) >= fyStart.getTime()).reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  const last = deposits[0];
  const borrowed = (loans.data || []).reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const interestPaid = (loans.data || []).reduce((sum, l) => sum + (Number(l.total_interest_paid) || 0), 0);
  const repaid = (loans.data || []).reduce((sum, l) => sum + (Number(l.total_principal_paid) || 0), 0) + interestPaid;
  // Their own money in deposits now, and what closed ones earned them.
  const mine = (list, inIt) => {
    const rows = (list || []).map((deposit) => ({ deposit, row: deposit.members.find((m) => String(m.member_id) === String(me)) })).filter((r) => r.row);
    const active = rows.filter((r) => r.deposit.status === "active");
    return {
      inIt: active.reduce((sum, r) => sum + (Number(inIt(r.row)) || 0), 0),
      count: active.length,
      interest: rows.filter((r) => r.deposit.status === "closed").reduce((sum, r) => sum + (Number(r.row.interest) || 0), 0),
    };
  };
  const myFds = mine(fds.data, (row) => row.amount);
  const myRds = mine(rds.data, (row) => row.deposited);
  const fundSavings = Number(fund.data?.total_saving_amount) || 0;
  const pending = (value) => (value === undefined ? "…" : value);

  return (
    <>
      <div className="sf-grid">
        <Stat highlight label="My savings" value={savings.data ? money(savings.data.total) : "…"} onClick={() => navigate("/admin/save")} />
        <Stat
          label="Loan outstanding"
          value={record.data ? money(record.data.pending_loan) : "…"}
          sub={record.data ? (Number(record.data.pending_loan) > 0 ? "still to repay" : "nothing owed") : null}
          onClick={() => navigate("/admin/loans")}
        />
        <Stat
          label="Pending dues"
          value={invoices.data ? money(dues) : "…"}
          sub={invoices.data ? (unpaid.length ? [plural(unpaid.length, "invoice", "invoices"), overdue ? `${overdue} overdue` : null].filter(Boolean).join(" · ") : "all paid up") : null}
          onClick={() => navigate("/admin/invoices")}
        />
      </div>

      <SectionTitle title="This month" />
      {current.loading ? null : current.invoice ? (
        <Card>
          <div className="sf-row" style={{ padding: 0, border: 0 }}>
            <div className="sf-row-main">
              <p className="sf-stat-label">Due {date(current.invoice.due_date)}</p>
              <p className="sf-stat-value">{money(amountDue(current.invoice) || current.invoice.total_amount)}</p>
            </div>
            <Pill tone={amountDue(current.invoice) <= 0 ? "good" : isOverdue(current.invoice) ? "bad" : "warn"}>
              {amountDue(current.invoice) <= 0 ? "Paid" : isOverdue(current.invoice) ? "Overdue" : "Unpaid"}
            </Pill>
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <Button onClick={() => navigate(`/admin/invoices/${current.invoice._id}`)}>See the invoice</Button>
          </div>
        </Card>
      ) : (
        <Empty title="Nothing due">You have no outstanding invoice.</Empty>
      )}

      <SectionTitle title="My numbers" />
      <div className="sf-grid">
        <Stat label="Saved this FY" value={savings.data ? money(savedThisFy) : "…"} sub={`since ${date(fyStart.getTime())}`} onClick={() => navigate("/admin/save")} />
        {seesFund ? (
          <Stat label="Share of the fund" value={savings.data ? percent(fundSavings > 0 ? savings.data.total / fundSavings : null) : "…"} sub="of everyone's savings" />
        ) : (
          // Their share would give the fund's total away.
          <Stat label="Loans taken" value={loans.data ? money(borrowed) : "…"} sub="in all, since joining" onClick={() => navigate("/admin/loans")} />
        )}
        <Stat
          label="Last deposit"
          value={savings.data ? (last ? money(last.amount) : "—") : "…"}
          sub={savings.data ? (last ? date(last.date) : "nothing saved yet") : null}
          onClick={() => navigate("/admin/save")}
        />
        <Stat label="Member since" value={pending(record.data ? (record.data.date ? date(record.data.date) : "—") : undefined)} sub={record.data ? since(record.data.date) : null} />
        <Stat
          label="Repaid on loans"
          value={loans.data ? money(repaid) : "…"}
          sub={loans.data ? (borrowed ? `of ${money(borrowed)} borrowed` : "no loans yet") : null}
          onClick={() => navigate("/admin/loans")}
        />
        <Stat label="Interest paid" value={loans.data ? money(interestPaid) : "…"} sub="to the fund, on your loans" onClick={() => navigate("/admin/loans")} />
        {can("fixed_deposits") ? (
          <Stat
            label="My fixed deposits"
            value={fds.data ? money(myFds.inIt) : "…"}
            sub={plural(myFds.count, "active FD", "active FDs")}
            credit={`${money(myFds.interest)} interest`}
            onClick={() => navigate("/admin/fixed-deposits")}
          />
        ) : null}
        {can("recurring_deposits") ? (
          <Stat
            label="My recurring deposits"
            value={rds.data ? money(myRds.inIt) : "…"}
            sub={plural(myRds.count, "active RD", "active RDs")}
            credit={`${money(myRds.interest)} interest`}
            onClick={() => navigate("/admin/recurring-deposits")}
          />
        ) : null}
      </div>
    </>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { auth, organisation, setOrganisation, seesEveryone, can, access } = useAuth();
  const seesFund = can("dashboard", "view_all");
  // Seeing every member's invoices is what the Fund half's "Due now" list is
  // built around, so it is also what decides which half opens first.
  const oversees = seesEveryone("invoices");
  const [chosen, setChosen] = useState(null);
  const half = !seesFund ? "personal" : chosen || (oversees ? "fund" : "personal");

  const fund = useFetch(`/api/organisation/getorganisationbyid/${organisation._id}`, { select: (body) => body.data });
  const dues = useFetch(`/api/invoice/${organisation._id}/invoices`, {
    skip: !(seesFund && oversees),
    select: (body) =>
      (Array.isArray(body) ? body : [])
        .filter((invoice) => invoice.status !== "paid" && amountDue(invoice) > 0)
        .sort((a, b) => (Number(a.due_date) || Infinity) - (Number(b.due_date) || Infinity)),
  });

  const [editing, setEditing] = useState(false);
  const [balance, setBalance] = useState("");
  const { busy, run } = useAction();

  const saveBalance = async () => {
    const ok = await run(() => axiosInstance.post(`/api/organisation/${organisation._id}/presentbalance`, { present_balance: Number(balance) }), {
      done: "Present balance saved",
    });
    if (ok) {
      setEditing(false);
      fund.reload();
    }
  };

  const switchFund = () => {
    localStorage.removeItem("organisation");
    setOrganisation({});
    navigate("/organisations", { replace: true });
  };

  // Their name, or the role they hold here when the account has none.
  const role = (access.role || auth.user.role || "").replace(/[-_]+/g, " ").trim();
  const roleTitle = role ? role[0].toUpperCase() + role.slice(1) : "";
  const greeting = `Hello, ${auth.user.first_name?.trim() || roleTitle || "there"}`;
  const subtitle = [roleTitle, organisation.name].filter(Boolean).join(" · ");

  if (access.loaded && !can("dashboard", "view")) {
    return (
      <Page title={greeting} subtitle={subtitle}>
        <Empty title="No dashboard for your role">Ask whoever runs the fund to turn on the dashboard for your role in Roles and permissions.</Empty>
      </Page>
    );
  }

  return (
    <Page
      title={greeting}
      subtitle={subtitle}
      actions={
        <Button variant="text" onClick={switchFund}>
          ⇄ Switch fund
        </Button>
      }
    >
      {seesFund ? (
        <div className="dash-switch" role="tablist" aria-label="Dashboard">
          {[
            ["fund", "Fund"],
            ["personal", "Personal"],
          ].map(([key, label]) => (
            <button key={key} role="tab" aria-selected={half === key} className={half === key ? "dash-switch-on" : ""} onClick={() => setChosen(key)}>
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {half === "fund" ? (
        <FundHalf
          fund={fund}
          dues={dues}
          oversees={oversees}
          mayEditBalance={can("settings", "edit")}
          onEditBalance={(current) => {
            setBalance(String(current ?? ""));
            setEditing(true);
          }}
        />
      ) : (
        <PersonalHalf seesFund={seesFund} fund={fund} />
      )}

      {editing ? (
        <Dialog
          title="Present balance"
          onClose={busy ? undefined : () => setEditing(false)}
          actions={
            <>
              <Button variant="text" onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" busy={busy} disabled={balance === "" || Number(balance) < 0 || Number.isNaN(Number(balance))} onClick={saveBalance}>
                Save
              </Button>
            </>
          }
        >
          <p className="sf-muted">The cash the fund is holding right now — in hand and in the bank. Nothing else can work it out, so it is set by hand and who set it is recorded.</p>
          <Field label="Amount">
            <input type="number" min="0" step="0.01" value={balance} autoFocus onChange={(event) => setBalance(event.target.value)} />
          </Field>
        </Dialog>
      ) : null}
    </Page>
  );
};

export default Dashboard;
